'use client';

import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { checkIfInLocalStorage } from '@eliadmin/core/src/utils/favorite-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { FiHeart } from 'react-icons/fi';

type AddToFavoritesButtonProps = {
  slug: string;
  className?: {
    button?: string;
    inner_container?: string;
    icon?: string;
    text?: string;
  };
  isFavorite?: boolean;
  productId?: number;
  showText?: boolean;
};

export function AddToFavoritesButton(props: AddToFavoritesButtonProps) {
  const hasCustomer = useCustomerStore(state => !!state.me?.authToken);
  const startedAsGuest = useRef(!hasCustomer);
  const [activated, setActivated] = useState(hasCustomer);
  useEffect(() => {
    if (hasCustomer) setActivated(true);
  }, [hasCustomer]);
  // Reconcile Core172's guest path while retaining this tenant's view and
  // locale/auth-scoped customer cache. Keep a pending mutation owner on logout.
  return hasCustomer || activated ? (
    <CustomerFavoritesButton {...props} refetchOnMount={startedAsGuest.current} />
  ) : (
    <GuestFavoritesButton {...props} />
  );
}

function GuestFavoritesButton(props: AddToFavoritesButtonProps) {
  const enabled = useGlobalStore(
    state => state.templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled,
  );
  const router = useRouter();
  const [isFavorite, setFavorite] = useState(!!props.isFavorite);
  useEffect(() => {
    const sync = () => setFavorite(
      enabled && props.productId != null
        ? !!checkIfInLocalStorage(props.productId)
        : !!props.isFavorite,
    );
    sync();
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [enabled, props.productId, props.isFavorite]);
  return (
    <FavoritesButtonView {...props} isFavorite={isFavorite} onClick={event => {
      event.stopPropagation();
      event.preventDefault();
      if (!enabled) return router.push(ROUTES.SIGN_IN());
      if (props.productId == null) return;
      const stored = JSON.parse(localStorage.getItem('favoriteProducts') || '[]') as number[];
      const favorites = stored.includes(props.productId)
        ? stored.filter(id => id !== props.productId)
        : [...stored, props.productId];
      localStorage.setItem('favoriteProducts', JSON.stringify(favorites));
      setFavorite(favorites.includes(props.productId));
      window.dispatchEvent(new Event('storage'));
    }} />
  );
}

function CustomerFavoritesButton(props: AddToFavoritesButtonProps & { refetchOnMount: boolean }) {
  const authToken = useCustomerStore(state => state.me?.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const queryClient = useQueryClient();
  const owner = JSON.stringify([locale, props.slug, authToken ?? null]);
  const sequence = useRef(0);
  const [optimistic, setOptimistic] = useState<{
    owner: string;
    sequence: number;
    isFavorite: boolean;
  } | null>(null);

  const { data: product } = useQuery({
    queryKey: [CacheKey.PRODUCT_DETAIL, locale, props.slug, authToken ?? null],
    queryFn: () =>
      fetchSlugModel({
        model: 'App\\Models\\Product',
        slug: props.slug,
        locale,
        authToken: authToken,
      }),
    enabled: !!authToken && !!props.slug,
    refetchOnWindowFocus: false,
    // Retry a mutation-invalidated cache after a failed background read, while
    // preserving fresh authenticated/SSR seeds without a blanket mount fetch.
    refetchOnMount: query => props.refetchOnMount || query.state.isInvalidated,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate, isPending, variables } = useMutation({
    mutationFn: (mutation: {
      request: Parameters<typeof addProductToFavorites>[0];
      owner: string;
      sequence: number;
      isFavorite: boolean;
    }) => addProductToFavorites(mutation.request),
    onSettled: async (_data, error, mutation) => {
      // Query observer options can change while the request is pending. Only
      // its immutable request variables identify the cache this result owns.
      const { request } = mutation;
      const queryKey = [CacheKey.PRODUCT_DETAIL, request.locale, request.productSlug, request.authToken];
      if (!error) {
        // This is provisional intent, not the server result of a toggle. Two
        // requests can succeed in either order, so always reconcile the origin.
        queryClient.setQueryData(queryKey, (cached: typeof product) => ({
          ...cached,
          data: {
            ...cached?.data,
            is_favorite: mutation.isFavorite,
          },
        }));
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'all' }),
        queryClient.invalidateQueries({
          queryKey: [CacheKey.FAVORITE_PRODUCTS, request.locale, request.authToken],
        }),
      ]);
      setOptimistic(current => current?.sequence === mutation.sequence ? null : current);
    },
  });

  const isFavorite = optimistic?.owner === owner
    ? optimistic.isFavorite
    : product?.data.is_favorite ?? !!props.isFavorite;

  function handleAddToFavorites(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    e.preventDefault();
    if (!authToken) return;
    const next = { owner, sequence: ++sequence.current, isFavorite: !isFavorite };
    setOptimistic(next);
    mutate({ ...next, request: { productSlug: props.slug, authToken, locale } });
  }

  // Keep this mutation owner mounted, but restore the same subscribed guest
  // presentation after logout (including cross-instance storage and cleanup).
  if (!authToken) return <GuestFavoritesButton {...props} />;

  return <FavoritesButtonView {...props} isFavorite={isFavorite} isMutating={isPending && variables?.owner === owner} onClick={handleAddToFavorites} />;
}

function FavoritesButtonView(props: AddToFavoritesButtonProps & {
  isFavorite?: boolean;
  isMutating?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}) {
  const { t } = useTranslations();
  const { isFavorite, isMutating } = props;
  return (
    <Button
      variant="outline"
      aria-label={isFavorite ? t('text532') : t('text531')}
      onClick={props.onClick}
      className={cn(props.className?.button, 'px-3')}
    >
      {isMutating ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <div className={cn('flex flex-row', props.className?.inner_container)}>
          <FiHeart
            className={cn(
              'h-5 w-5 cursor-pointer transition-colors duration-200 ease-in-out  hover:text-muted-foreground',
              'text-foreground',
              isFavorite
                ? 'fill-destructive-foreground stroke-destructive-foreground'
                : 'fill-none',
              props.className?.icon,
            )}
            aria-hidden="true"
            strokeWidth={2}
          />
          {props.showText && (
            <span className={cn('link ml-2 underline', props.className?.text)}>
              {isFavorite ? t('text532') : t('text531')}
            </span>
          )}
        </div>
      )}
    </Button>
  );
}
